import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import {
  Shield,
  AlertCircle,
  FileText,
  Copy,
} from 'lucide-react';
import {
  Alert,
  BackLink,
  Card,
  Button,
  Heading,
  BodyText,
  Skeleton,
  Table,
  Badge,
  formatDateTime as formatInstant,
} from '@aaimplatform/ui';
import type { TableColumn } from '@aaimplatform/ui';
import { usePledgeDetail } from '../../hooks/usePledges';
import { RouterLinkComponent } from '../../lib/router-link';
import type {
  PledgeAdmissibleAction,
  PledgeSelectedAccountDetail,
  PledgeSelectedHoldingDetail,
  PledgeConsentProof,
  PledgeMemberSignature,
} from '../../hooks/usePledges';
import { PledgeActions } from './PledgeActions';
import {
  ACCOUNT_TYPE_LABEL,
  ASSET_CLASS_LABEL,
  BACK_TO_ACTIVE,
  BACK_TO_INBOX,
  CONSENT_METHOD_LABEL,
  ChaTierBadge,
  EligibilityBadge,
  EnumText,
  PledgeStatusBadge,
  formatCurrencyFull as formatCurrency,
} from './pledge-display';
import type { PledgeBackTargetState } from './pledge-display';
import type { components as CollateralPledgeComponents } from '@aaimplatform/institutional-types/collateral-pledge';

type AttestationMethod = CollateralPledgeComponents['schemas']['AttestationMethod'];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

/**
 * A ratio at one decimal, the admin-portal rule for LTV figures
 * (pages/monitoring/display-format.ts): a holding's LTV printed '65%' here
 * beside '80.0%' on the monitoring pages (r3 fi-cons-2). Amounts print
 * through the pledge pages' one currency formatter (pledge-display.tsx).
 */
const PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '--';
  return PERCENT_FORMATTER.format(value);
}

/**
 * An instant on this page, with its year and a zone abbreviation, to the
 * minute: "Sep 11, 2026, 4:21 PM PDT". The consent record reads to the second
 * ({@link formatConsentTimestamp}). The page printed "04:21 PM" with no zone,
 * which read three different ways across the pledge surfaces (fi-g1-6).
 */
function formatDateTime(value: string | null | undefined): string {
  if (!value) return '--';
  return formatInstant(value, { seconds: false });
}

/** The consent timestamp is evidentiary, so it carries seconds and a zone. */
function formatConsentTimestamp(value: string | null | undefined): string {
  if (!value) return '--';
  return formatInstant(value);
}

// ---------------------------------------------------------------------------
// Detail field
// ---------------------------------------------------------------------------

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-0.5">
        {label}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Holding table columns (nested under each pledged account)
// ---------------------------------------------------------------------------

// Format a holding quantity. Whole-number quantities render without
// trailing zeros for visual cleanliness; fractional shares (e.g., 12.5
// AAPL) and crypto fractions (e.g., 0.5234 BTC) preserve up to 4
// decimals of precision.
function formatQuantity(quantity: number): string {
  if (!Number.isFinite(quantity)) return '--';
  if (Number.isInteger(quantity)) {
    return quantity.toLocaleString('en-US');
  }
  return quantity.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  });
}

/**
 * Label for each capture method. Keyed on the generated AttestationMethod
 * enum, so a method added to collateral-pledge.openapi.yaml fails tsc here
 * until it has a label. A stored value outside the enum prints "Unrecognized"
 * through EnumText, never the raw code.
 */
const ATTESTATION_METHOD_LABEL: Record<AttestationMethod, string> = {
  ADVISOR_KEYSTROKE: 'Advisor keystroke attestation',
  MEMBER_SELF_ATTESTED: 'Customer self-attestation',
};

/**
 * Display names for the valuation sources consumer-api records on a pledge
 * line (GH #4165): `attom` where ATTOM's valuation set the value, `manual`
 * where the customer or an advisor stated it. The label names the provider
 * only: for a member-added property the value is ATTOM's valuation less the
 * liens the customer stated (manual-assets.routes.ts), so it is not ATTOM's
 * figure as such. The field is free text
 * (platform-admin PledgeSelectedHoldingDetail.valuationSource), so any other
 * recorded source prints as recorded.
 */
const VALUATION_SOURCE_LABEL: ReadonlyMap<string, string> = new Map([
  ['attom', 'ATTOM'],
  ['manual', 'Stated value'],
]);

function valuationSourceLabel(source: string): string {
  return VALUATION_SOURCE_LABEL.get(source.toLowerCase()) ?? source;
}

/** The attestation badge of one pledged line (rbac-06 / #1279). */
function attestationProvenance(row: PledgeSelectedHoldingDetail) {
  if (row.fiduciaryAttestation) {
    return (
      <div>
        <Badge variant="info" size="small" icon={<Shield className="w-3 h-3" />}>
          Mission Trust attested
        </Badge>
        {row.attestationMethod ? (
          <div className="mt-1 text-xs font-sans text-secondary leading-normal">
            <EnumText map={ATTESTATION_METHOD_LABEL} value={row.attestationMethod} />
          </div>
        ) : null}
        {row.attestationAsOfDate ? (
          <div className="text-xs font-sans text-tertiary leading-normal">
            As of {formatDateTime(row.attestationAsOfDate)}
          </div>
        ) : null}
      </div>
    );
  }
  if (row.attestationMethod) {
    return (
      <Badge variant="neutral" size="small">
        <EnumText map={ATTESTATION_METHOD_LABEL} value={row.attestationMethod} />
      </Badge>
    );
  }
  return (
    <Badge variant="neutral" size="small">
      Aggregator
    </Badge>
  );
}

const HOLDING_COLUMNS: TableColumn<PledgeSelectedHoldingDetail>[] = [
  {
    key: 'securityName',
    label: 'Security',
    render: (_value, row) => (
      <div>
        <div className="text-sm font-sans font-medium text-primary leading-normal">
          {row.securityName}
        </div>
        {row.tickerSymbol && (
          <div className="text-xs font-mono text-tertiary leading-normal">
            {row.tickerSymbol}
          </div>
        )}
      </div>
    ),
  },
  {
    key: 'assetClass',
    label: 'Asset Class',
    render: (_value, row) => (
      <EnumText
        map={ASSET_CLASS_LABEL}
        value={row.assetClass}
        className="text-sm font-sans text-secondary leading-normal"
      />
    ),
  },
  {
    // Articulates WHAT is pledged. The FI sees the exact unit count
    // (shares, ETF units, crypto fractions) alongside the security name.
    key: 'quantity',
    label: 'Quantity',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatQuantity(row.quantity)}
      </span>
    ),
  },
  {
    key: 'marketValue',
    label: 'Market Value at Pledge',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatCurrency(row.marketValue)}
      </span>
    ),
  },
  {
    key: 'ltvRatio',
    label: 'LTV',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
        {formatPercent(row.ltvRatio)}
      </span>
    ),
  },
  {
    key: 'pledgeableAmount',
    label: 'Pledgeable at Pledge',
    align: 'right',
    render: (_value, row) => (
      <span className="text-sm font-mono text-primary tabular-nums leading-normal">
        {formatCurrency(row.pledgeableAmount)}
      </span>
    ),
  },
  {
    key: 'eligible',
    label: 'Eligible',
    align: 'center',
    render: (_value, row) => <EligibilityBadge eligible={row.eligible} />,
  },
  {
    // Per-line provenance (rbac-06 / #1279). A direct-attested (Mission Trust)
    // position carries a fiduciary advisor attestation snapshotted at pledge
    // time; an aggregator-sourced (Quiltt/MX) position does not. Surfacing the
    // distinction per line lets an FI reviewer see exactly which holdings were
    // advisor-attested in a mixed pledge. A null or false fiduciaryAttestation
    // also covers a member self-attested holding, which the capture method
    // (the AttestationMethod enum) tells apart from an aggregator line; the
    // method and the as-of date print as text, never in a title attribute.
    // Beneath it, the source of the market value the line was pledged
    // against, snapshot at pledge time (GH #4165); a line with no recorded
    // source prints none, which claims less than a stated value.
    key: 'fiduciaryAttestation',
    label: 'Provenance',
    render: (_value, row) => (
      <div>
        {attestationProvenance(row)}
        {row.valuationSource ? (
          <div className="mt-1 text-xs font-sans text-secondary leading-normal">
            Value source: {valuationSourceLabel(row.valuationSource)}
          </div>
        ) : null}
      </div>
    ),
  },
];

// ---------------------------------------------------------------------------
// Consent proof card
// ---------------------------------------------------------------------------

function ConsentProofCard({ consent }: { consent: PledgeConsentProof }) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        {/* Decoration, not a CTA or an alert: tertiary, never the red
            accent (fi-g1-9). */}
        <Shield className="w-4 h-4 text-tertiary" aria-hidden="true" />
        <Heading level={3} size="card">
          Consent Proof
        </Heading>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        <DetailField label="Acknowledged">
          <BodyText variant="ui" className="text-primary">
            {consent.consentAcknowledged ? 'Yes' : 'No'}
          </BodyText>
        </DetailField>
        <DetailField label="Timestamp">
          <BodyText variant="ui" className="text-primary">
            {formatConsentTimestamp(consent.consentTimestamp)}
          </BodyText>
        </DetailField>
        <DetailField label="IP Address">
          <BodyText variant="ui" className="text-primary font-mono text-sm">
            {consent.consentIpAddress ?? '--'}
          </BodyText>
        </DetailField>
        <DetailField label="Method">
          <BodyText variant="ui" className="text-primary">
            <EnumText map={CONSENT_METHOD_LABEL} value={consent.consentMethod} />
          </BodyText>
        </DetailField>
        <DetailField label="Terms Version">
          <BodyText variant="ui" className="text-primary">
            {consent.consentTermsVersion ?? '--'}
          </BodyText>
        </DetailField>
        <div className="sm:col-span-2">
          <DetailField label="User Agent">
            <BodyText variant="ui" className="text-secondary text-sm break-all">
              {consent.consentUserAgent ?? '--'}
            </BodyText>
          </DetailField>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Customer signature card
// ---------------------------------------------------------------------------

/**
 * The customer's signature on the pledge's agreement: when it was captured,
 * and one label when it carries no drawn mark, so a reviewer knows before
 * accepting (Rob's ruling, 2026-10-11). The detail read carries the time and
 * the kind alone; this page shows no image of a signature.
 */
function MemberSignatureCard({ signature }: { signature: PledgeMemberSignature }) {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        {/* Decoration, as on the Consent Proof card: tertiary. */}
        <FileText className="w-4 h-4 text-tertiary" aria-hidden="true" />
        <Heading level={3} size="card">
          Customer signature
        </Heading>
      </div>
      <DetailField label="Signed">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {/* Evidentiary, like the consent timestamp: to the second, with a zone. */}
          <BodyText variant="ui" className="text-primary">
            {formatConsentTimestamp(signature.signedAt)}
          </BodyText>
          {signature.kind === 'TYPED' && (
            <Badge variant="neutral" size="small">
              Signed with typed name
            </Badge>
          )}
        </div>
      </DetailField>
    </Card>
  );
}

/**
 * Stands in the signature card's place when the detail read could not read the
 * customer's signature (PledgeDetailResponse.memberSignatureUnreadable). The
 * server then lists every act the pledge admits except Accept, and this says
 * why (Rob's ruling, 2026-10-12): a reviewer does not accept a signature the
 * page could not describe. A failed read says nothing about whether the
 * customer has signed, so the notice speaks of the signature's status. The
 * response does not say whether this pledge would have admitted Accept, so the
 * middle sentence states the rule and promises no Accept after a reload. The
 * title is a heading because the notice takes a section card's place.
 */
function MemberSignatureUnreadableNotice() {
  return (
    <Alert type="warning" title="Customer signature" headingLevel={3}>
      Unable to load the customer&apos;s signature status. Accept is not offered while it cannot
      be loaded. Reload this page to try again.
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// PledgeDetailPage
// ---------------------------------------------------------------------------

// F-AP-PLEDGE-BACKNAV-001 (2026-05-16): source-aware back navigation.
// Callers may pass location.state.from = { pathname, label } when
// navigating to /pledges/:pledgeId so the back-link returns to the
// originating surface (Tasks, Alerts, ActivePledges, PledgeInbox).
//
// A deep link carries no state. The fallback follows the pledge's status to
// the list that actually shows it: ACKNOWLEDGED and ACTIVE are Active Pledges
// rows, and the Inbox excludes both (PledgeInbox INBOX_EXCLUDED_STATUSES). It
// used to return every deep link to the Inbox under the label "Back to Pledge
// Review", which for an ACTIVE pledge landed on a list that did not hold it.
type BackNavState = {
  from?: { pathname: string; label?: string };
};

function defaultBackFor(status: string | undefined): PledgeBackTargetState {
  return status === 'ACKNOWLEDGED' || status === 'ACTIVE' ? BACK_TO_ACTIVE : BACK_TO_INBOX;
}

/** The admissible actions that are an FI review decision. Release is not one. */
const REVIEW_ACTIONS: readonly PledgeAdmissibleAction[] = [
  'PLEDGE_ACKNOWLEDGED',
  'PLEDGE_ACCEPTED',
  'PLEDGE_DECLINED',
  'PLEDGE_RETURNED',
];

/**
 * "Pledge Review" only while the FI has a decision to make. When the detail
 * read carries admissibleActions (GH #2599), the heading follows the same list
 * PledgeActions renders from, so it reads "Pledge Review" exactly when a review
 * panel is on the page. When the server omits the field (a pinned recipe it
 * could not read), the heading falls back to the pledge status.
 */
export function pledgeHeading(
  status: string,
  acknowledgedBy: string | null | undefined,
  admissibleActions?: readonly PledgeAdmissibleAction[],
): string {
  if (admissibleActions) {
    return admissibleActions.some((action) => REVIEW_ACTIONS.includes(action))
      ? 'Pledge Review'
      : 'Pledge';
  }
  if (status === 'PENDING' || status === 'ACKNOWLEDGED') return 'Pledge Review';
  if (status === 'AWAITING_SIGNATURE' && !acknowledgedBy) return 'Pledge Review';
  return 'Pledge';
}

export function PledgeDetailPage() {
  const { pledgeId } = useParams<{ pledgeId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: response, isLoading, error } = usePledgeDetail(pledgeId);
  const pledgeStatus = response?.pledge?.status;

  const backTarget = useMemo(() => {
    const state = (location.state as BackNavState | null) ?? null;
    if (state?.from?.pathname) {
      return {
        pathname: state.from.pathname,
        label: state.from.label ?? 'Back',
      };
    }
    return defaultBackFor(pledgeStatus);
  }, [location.state, pledgeStatus]);

  // The kit BackLink, the back link every detail page renders. This page
  // built its own gray button, and Compare Deals a red tertiary Button with
  // the arrow after the label (r3 fi-cons-5).
  const backLink = (
    <div className="mb-6">
      <BackLink href={backTarget.pathname} LinkComponent={RouterLinkComponent}>
        {backTarget.label}
      </BackLink>
    </div>
  );

  // A deep link carries no origin, so the layout titled the page and lit the
  // nav from the '/pledges' prefix ("Pledge Inbox") while the back link, which
  // knows the status, read "Back to Active Pledges" (fi-g1-2). Once the status
  // is read, the status-aware origin is written into the history entry, and
  // the top-bar title, breadcrumbs, nav highlight and back link all derive from
  // that one value.
  const hasOrigin = Boolean((location.state as BackNavState | null)?.from?.pathname);
  useEffect(() => {
    if (hasOrigin || !pledgeStatus) return;
    void navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: { from: defaultBackFor(pledgeStatus) } },
    );
  }, [hasOrigin, pledgeStatus, navigate, location.pathname, location.search, location.hash]);

  const pledge = response?.pledge;

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div>
        {backLink}
        <div className="space-y-6">
          <Card>
            <Skeleton className="h-8 w-48 mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i}>
                  <Skeleton className="h-3 w-20 mb-2" />
                  <Skeleton className="h-6 w-28" />
                </div>
              ))}
            </div>
          </Card>
          <Card>
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-48 w-full" />
          </Card>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Error state
  // ---------------------------------------------------------------------------

  if (error || !pledge) {
    return (
      <div>
        {backLink}
        <Card>
          <div className="flex items-center gap-3 mb-2">
            <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
            <Heading level={3} size="card" className="text-error">
              Unable to load pledge
            </Heading>
          </div>
          <BodyText variant="ui" className="text-secondary">
            {error?.message ?? 'Pledge not found or could not be retrieved.'}
          </BodyText>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div>
      {/* Back navigation (F-AP-PLEDGE-BACKNAV-001 source-aware target) */}
      {backLink}

      {/* Header card */}
      <Card className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-5 h-5 text-tertiary" aria-hidden="true" />
              <Heading level={2} size="subsection" className="font-sans">
                {pledgeHeading(pledge.status, pledge.acknowledgedBy, response?.admissibleActions)}
              </Heading>
              <PledgeStatusBadge status={pledge.status} />
            </div>
            <div className="mt-2">
              <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-1">
                Collateral Package ID
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-mono font-semibold text-primary tabular-nums leading-tight tracking-wide">
                  {pledge.collateralPackageId}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                  onClick={() => {
                    void navigator.clipboard.writeText(pledge.collateralPackageId);
                  }}
                  aria-label="Copy collateral package ID"
                >
                  Copy
                </Button>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-label-xs font-sans font-semibold uppercase text-tertiary">
                  Internal ID
                </span>
                <BodyText variant="ui" className="text-secondary text-xs font-mono">
                  {pledge.id}
                </BodyText>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={Copy}
                  onClick={() => {
                    void navigator.clipboard.writeText(pledge.id);
                  }}
                  aria-label="Copy internal pledge ID"
                >
                  Copy
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Customer and pledge figures. No Institution field (GH #678,
            operator ruling 2026-10-23): the pledge is always the reader's own
            institution, so `fiName` told the reader nothing. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-6 pt-6 border-t border-recessed">
          <DetailField label="Customer">
            <BodyText variant="ui" className="text-primary font-medium">
              {pledge.memberName ?? pledge.memberEmail}
            </BodyText>
            {pledge.memberName && (
              <BodyText variant="ui" className="text-secondary text-sm">
                {pledge.memberEmail}
              </BodyText>
            )}
          </DetailField>

          <DetailField label="Total Collateral">
            <BodyText variant="ui" className="text-primary font-medium font-mono tabular-nums">
              {formatCurrency(pledge.totalCollateralValue)}
            </BodyText>
          </DetailField>

          <DetailField label="Total Pledgeable">
            <BodyText variant="ui" className="text-primary font-medium font-mono tabular-nums">
              {formatCurrency(pledge.totalPledgeableAmount)}
            </BodyText>
          </DetailField>

          <DetailField label="CHA Tier">
            <ChaTierBadge tier={pledge.currentChaTier} />
          </DetailField>

          <DetailField label="Created">
            <BodyText variant="ui" className="text-primary">
              {formatDateTime(pledge.createdAt)}
            </BodyText>
          </DetailField>

          {pledge.expiresAt && (
            <DetailField label="Expires">
              <BodyText variant="ui" className="text-primary">
                {formatDateTime(pledge.expiresAt)}
              </BodyText>
            </DetailField>
          )}

          {pledge.acknowledgedBy && (
            <DetailField label="Acknowledged By">
              <BodyText variant="ui" className="text-primary">
                {pledge.acknowledgedBy}
              </BodyText>
            </DetailField>
          )}

          {pledge.loanReferenceId && (
            <DetailField label="Loan Reference">
              <BodyText variant="ui" className="text-primary font-mono">
                {pledge.loanReferenceId}
              </BodyText>
            </DetailField>
          )}

          {pledge.acceptedBy && (
            <DetailField label="Accepted By">
              <BodyText variant="ui" className="text-primary">
                {pledge.acceptedBy}
              </BodyText>
            </DetailField>
          )}

          {pledge.acceptedAt && (
            <DetailField label="Accepted At">
              <BodyText variant="ui" className="text-primary">
                {formatDateTime(pledge.acceptedAt)}
              </BodyText>
            </DetailField>
          )}

          {pledge.declinedBy && (
            <DetailField label="Declined By">
              <BodyText variant="ui" className="text-primary">
                {pledge.declinedBy}
              </BodyText>
            </DetailField>
          )}

          {pledge.releasedBy && (
            <DetailField label="Released By">
              <BodyText variant="ui" className="text-primary">
                {pledge.releasedBy}
              </BodyText>
            </DetailField>
          )}
        </div>

        {/* Decline reason */}
        {pledge.declinedReason && (
          <div className="mt-4 pt-4 border-t border-recessed">
            <DetailField label="Decline Reason">
              <BodyText variant="ui" className="text-error">
                {pledge.declinedReason}
              </BodyText>
            </DetailField>
          </div>
        )}

        {/* Return reason */}
        {pledge.returnReason && (
          <div className="mt-4 pt-4 border-t border-recessed">
            <DetailField label="Revision Instructions">
              <BodyText variant="ui" className="text-warning">
                {pledge.returnReason}
              </BodyText>
            </DetailField>
          </div>
        )}

        {/* Release reason */}
        {pledge.releaseReason && (
          <div className="mt-4 pt-4 border-t border-recessed">
            <DetailField label="Release Reason">
              <BodyText variant="ui" className="text-secondary">
                {pledge.releaseReason}
              </BodyText>
            </DetailField>
          </div>
        )}

        {/* Notes */}
        {pledge.notes && (
          <div className="mt-4 pt-4 border-t border-recessed">
            <DetailField label="Notes">
              <BodyText variant="ui" className="text-secondary">
                {pledge.notes}
              </BodyText>
            </DetailField>
          </div>
        )}
      </Card>

      {/* Accounts with optional nested holdings */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Heading level={3} size="card">
            Selected Accounts ({pledge.accounts.length})
          </Heading>
        </div>
        {/*
          Account and holding figures are the pledge-time snapshot
          (PledgeSelectedAccountDetail.currentBalance: "balance at pledge
          time"). The monitoring sweep revalues an ACTIVE pledge's Total
          Collateral and Total Pledgeable (GH #2383), so on an ACTIVE pledge the
          header totals can differ from the sum of these rows.
        */}
        <BodyText variant="ui" className="text-secondary text-sm mb-4">
          {pledge.status === 'ACTIVE'
            ? 'Recorded when the customer pledged. Total Collateral and Total Pledgeable above are revalued as prices move, so they can differ from these figures.'
            : 'Recorded when the customer pledged.'}
        </BodyText>
        <div className="space-y-6">
          {pledge.accounts.map((account: PledgeSelectedAccountDetail) => (
            <div key={account.id} className="border border-recessed rounded-sm">
              {/* Account header row -- summary fields */}
              <div className="p-4 bg-surface-recessed">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 items-start">
                  <div>
                    <div className="text-sm font-sans font-medium text-primary leading-normal">
                      {account.accountName}
                    </div>
                    {account.institutionName && (
                      <div className="text-xs font-sans text-tertiary leading-normal">
                        {account.institutionName}
                      </div>
                    )}
                  </div>
                  <DetailField label="Type">
                    <EnumText
                      map={ACCOUNT_TYPE_LABEL}
                      value={account.accountType}
                      className="text-sm font-sans text-secondary leading-normal"
                    />
                  </DetailField>
                  <DetailField label="Asset class">
                    <EnumText
                      map={ASSET_CLASS_LABEL}
                      value={account.assetClass}
                      className="text-sm font-sans text-secondary leading-normal"
                    />
                  </DetailField>
                  {/*
                    The pledge basis for a cash or deposit account is its
                    available balance; otherwise the posted balance stands. The
                    label names which one printed, so the figure is never read
                    as the other.
                  */}
                  <DetailField
                    label={
                      account.availableBalance !== undefined && account.availableBalance !== null
                        ? 'Available balance at pledge'
                        : 'Balance at pledge'
                    }
                  >
                    <div className="text-sm font-mono text-primary tabular-nums leading-normal">
                      {formatCurrency(account.availableBalance ?? account.currentBalance)}
                    </div>
                  </DetailField>
                  <DetailField label="Pledgeable at pledge">
                    <div className="text-sm font-mono text-primary tabular-nums leading-normal">
                      {formatCurrency(account.pledgeableAmount)}
                    </div>
                  </DetailField>
                  <DetailField label="Eligibility">
                    <EligibilityBadge eligible={account.eligible} />
                  </DetailField>
                </div>
              </div>

              {/* Holdings sub-table OR "Entire account" indicator */}
              {account.holdings && account.holdings.length > 0 ? (
                <div className="p-4 border-t border-recessed">
                  <div className="text-label-xs font-sans font-semibold uppercase text-tertiary mb-2">
                    Selected Holdings ({account.holdings.length})
                  </div>
                  <Table<PledgeSelectedHoldingDetail>
                    data={account.holdings}
                    columns={HOLDING_COLUMNS}
                    hoverable
                  />
                </div>
              ) : (
                <div className="p-4 border-t border-recessed">
                  <BodyText variant="ui" className="text-secondary text-sm italic">
                    Entire account pledged
                  </BodyText>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Consent proof */}
      {pledge.consent && (
        <div className="mb-6">
          <ConsentProofCard consent={pledge.consent} />
        </div>
      )}

      {/* Customer signature */}
      {pledge.memberSignature && (
        <div className="mb-6">
          <MemberSignatureCard signature={pledge.memberSignature} />
        </div>
      )}
      {!pledge.memberSignature && response?.memberSignatureUnreadable && (
        <div className="mb-6">
          <MemberSignatureUnreadableNotice />
        </div>
      )}

      {/* Action panels -- the actions the server lists as admissible (GH #2599) */}
      {pledgeId && (
        <PledgeActions pledgeId={pledgeId} admissibleActions={response?.admissibleActions} />
      )}
    </div>
  );
}
